import { CheckIcon } from "@heroicons/react/24/outline"
import { Chrome } from "@uiw/react-color"
import React, { useState, useEffect, useCallback } from "react"

import { Button } from "../../../../components/shared/Buttons"
import Typography from "../../../../components/shared/Typography"
import ConfirmSaveModal from "../../components/shared/ConfirmSaveModal"
import { generateColorPalette, getColorPresets, isValidHexColor } from "../../utils/enhancedColorUtils"
import { useWebsiteBuilderV2 } from "../../WebsiteBuilderV2Context"

const ColorThemeForm = ({ theme, onUpdate, onClose }) => {
  const { clearPreview } = useWebsiteBuilderV2()
  const [activeTab, setActiveTab] = useState("presets")
  const [selectedPreset, setSelectedPreset] = useState(null)
  const [customHex, setCustomHex] = useState(theme?.colors?.customHex || "#CC34BD")
  const [showConfirmModal, setShowConfirmModal] = useState(false)

  // Get color presets from enhanced color utils
  const colorPresets = [
    ...getColorPresets(),
    {
      id: "custom",
      name: "Custom",
      description: "Make it yours: vibrant, playful, and uniquely you!",
      primaryColor: "#32CD32"
    }
  ]

  // Effect to sync form state with theme prop when it changes (e.g., when form is reopened)
  useEffect(() => {
    setCustomHex(theme?.colors?.customHex || "#CC34BD")

    // Determine which tab should be active based on the theme
    // Simple logic: if customHex is not null, open Custom tab; otherwise open Preset Themes tab

    // Check if we have custom colors (customHex is not null)
    const hasCustomColors =
      theme?.colors?.customHex !== null &&
      theme?.colors?.customHex !== undefined &&
      theme?.colors?.customHex !== "#CC34BD"

    if (hasCustomColors) {
      // If we have custom colors, go to custom tab and clear preset selection
      setActiveTab("custom")
      setSelectedPreset(null)
    } else {
      // Otherwise, go to presets tab
      setActiveTab("presets")

      // Use selectedPreset field to determine which preset is selected
      if (theme?.colors?.selectedPreset && colorPresets.find((p) => p.id === theme.colors.selectedPreset)) {
        setSelectedPreset(theme.colors.selectedPreset)
      } else {
        // Fallback: try to match current colors with a preset
        const currentColors = [
          theme?.colors?.primary,
          theme?.colors?.primaryDark,
          theme?.colors?.secondary,
          theme?.colors?.secondaryDark,
          theme?.colors?.accent
        ]

        const matchingPreset = colorPresets.find((preset) => {
          // Generate the palette for this preset to compare
          const presetPalette = generateColorPalette(preset.primaryColor)
          const presetColors = [
            presetPalette.primary,
            presetPalette.bgDark,
            presetPalette.bgMedium,
            presetPalette.bgLight,
            presetPalette.bgExtraLight
          ]

          return currentColors.every((color, index) => {
            if (!color || !presetColors[index]) return false
            const normalizedCurrent = color.replace("#", "").toUpperCase()
            const normalizedPreset = presetColors[index].replace("#", "").toUpperCase()
            return normalizedCurrent === normalizedPreset
          })
        })

        setSelectedPreset(matchingPreset?.id || null)
      }
    }
  }, [theme]) // Re-run this effect whenever the theme prop changes

  // Function to apply temporary preview (doesn't save to database)
  const applyTemporaryPreview = useCallback(
    (colors) => {
      // Create a temporary theme object for preview only
      const tempTheme = {
        ...theme,
        colors: {
          ...theme?.colors,
          ...colors
        }
      }

      // Pass the temporary theme to the preview component
      // This will be handled by the parent component's preview system
      onUpdate(tempTheme, { temporary: true })
    },
    [theme, onUpdate]
  )

  // Debounced function to apply custom color changes for preview only
  const debouncedApplyCustomColor = useCallback(
    (() => {
      let timeoutId
      return (hex) => {
        clearTimeout(timeoutId)
        timeoutId = setTimeout(() => {
          if (hex.match(/^#[0-9A-Fa-f]{6}$/)) {
            const palette = generateColorPalette(hex)
            applyTemporaryPreview({
              primary: palette.primary,
              primaryDark: palette.primaryDark,
              secondary: palette.secondary,
              secondaryDark: palette.secondaryDark,
              accent: palette.accent,
              textLight: palette.textLight,
              textDark: palette.textDark,
              bgLight: palette.bgLight,
              bgDark: palette.bgDark,
              bgMedium: palette.bgMedium,
              bgExtraLight: palette.bgExtraLight,
              customHex: hex,
              selectedPreset: null // Clear selected preset when using custom colors
            })
          }
        }, 300) // 300ms delay
      }
    })(),
    [applyTemporaryPreview]
  )

  // Apply custom color changes with debouncing
  useEffect(() => {
    if (activeTab === "custom" && customHex.match(/^#[0-9A-Fa-f]{6}$/)) {
      // Clear selected preset when using custom colors
      setSelectedPreset(null)
      debouncedApplyCustomColor(customHex)
    }
  }, [customHex, activeTab, debouncedApplyCustomColor])

  const handlePresetSelect = (presetId) => {
    setSelectedPreset(presetId)
    // Clear custom hex when selecting a preset
    setCustomHex("#CC34BD")

    // Generate palette from the preset's primary color
    const selectedPresetData = colorPresets.find((preset) => preset.id === presetId)
    const palette = generateColorPalette(selectedPresetData.primaryColor)

    applyTemporaryPreview({
      primary: palette.primary,
      primaryDark: palette.primaryDark,
      secondary: palette.secondary,
      secondaryDark: palette.secondaryDark,
      accent: palette.accent,
      textLight: palette.textLight,
      textDark: palette.textDark,
      bgLight: palette.bgLight,
      bgDark: palette.bgDark,
      bgMedium: palette.bgMedium,
      bgExtraLight: palette.bgExtraLight,
      selectedPreset: presetId,
      customHex: null // Clear custom hex when selecting preset
    })
  }

  const handleSave = () => {
    // Validate before showing modal
    if (activeTab === "presets") {
      const selectedPresetData = colorPresets.find((preset) => preset.id === selectedPreset)
      if (!selectedPresetData) return
    } else {
      // Validate custom hex color
      if (!isValidHexColor(customHex)) {
        alert("Please enter a valid hex color (e.g., #FF0000)")
        return
      }
    }

    // Show confirmation modal
    setShowConfirmModal(true)
  }

  const handleConfirmSave = () => {
    setShowConfirmModal(false)

    if (activeTab === "presets") {
      const selectedPresetData = colorPresets.find((preset) => preset.id === selectedPreset)

      if (!selectedPresetData) return

      const palette = generateColorPalette(selectedPresetData.primaryColor)

      // Save the actual theme data (not temporary)
      const saveData = {
        colors: {
          primary: palette.primary,
          primaryDark: palette.primaryDark,
          secondary: palette.secondary,
          secondaryDark: palette.secondaryDark,
          accent: palette.accent,
          textLight: palette.textLight,
          textDark: palette.textDark,
          bgLight: palette.bgLight,
          bgDark: palette.bgDark,
          bgMedium: palette.bgMedium,
          bgExtraLight: palette.bgExtraLight,
          white: "#FFFFFF",
          greyLightest: palette.bgExtraLight,
          greyLight: palette.bgLight,
          grey: palette.bgMedium,
          greyDark: palette.bgDark,
          greyDarkest: palette.textDark,
          black: "#000000",
          reviewStars: palette.reviewStars,
          selectedPreset: selectedPreset,
          customHex: null // Clear custom hex when saving preset
        }
      }

      onUpdate(saveData)

      // Close the form after saving
      onClose()
    } else {
      // For custom, generate a palette based on the hex color
      const palette = generateColorPalette(customHex)

      // Save the actual theme data (not temporary)
      const saveData = {
        colors: {
          primary: palette.primary,
          primaryDark: palette.primaryDark,
          secondary: palette.secondary,
          secondaryDark: palette.secondaryDark,
          accent: palette.accent,
          textLight: palette.textLight,
          textDark: palette.textDark,
          bgLight: palette.bgLight,
          bgDark: palette.bgDark,
          bgMedium: palette.bgMedium,
          bgExtraLight: palette.bgExtraLight,
          white: "#FFFFFF",
          greyLightest: palette.bgExtraLight,
          greyLight: palette.bgLight,
          grey: palette.bgMedium,
          greyDark: palette.bgDark,
          greyDarkest: palette.textDark,
          black: "#000000",
          reviewStars: palette.reviewStars,
          customHex: customHex,
          selectedPreset: null // Clear selected preset when using custom colors
        }
      }

      onUpdate(saveData)

      // Close the form after saving
      onClose()
    }
  }

  const handleCancelClick = () => {
    clearPreview()
    onClose()
  }

  return (
    <div className="w-full">
      {/* Tabs */}
      <div className="flex border-b border-gray-light px-8">
        <button
          className={`px-4 py-3 text-base font-bold ${
            activeTab === "presets" ? "border-b-2 border-teal text-gray-dark" : "text-teal hover:text-teal-dark"
          }`}
          onClick={() => setActiveTab("presets")}>
          Preset Themes
        </button>
        <button
          className={`px-4 py-3 text-base font-normal ${
            activeTab === "custom" ? "border-b-2 border-teal text-gray-dark" : "text-teal hover:text-teal-dark"
          }`}
          onClick={() => {
            setActiveTab("custom")
            setSelectedPreset(null) // Clear selected preset when switching to custom
          }}>
          Custom
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-8 py-6">
        {activeTab === "presets" && (
          <div className="space-y-4">
            {colorPresets.map((preset) => {
              // Determine if this preset should be shown as selected
              // A preset is selected if:
              // 1. We're not using custom colors (customHex is null) AND
              // 2. Either the selectedPreset state matches this preset's ID (temporary selection)
              //    OR the stored theme's selectedPreset matches this preset's ID (saved selection)

              // Check if we have stored custom colors
              const hasStoredCustomColors =
                theme?.colors?.customHex !== null &&
                theme?.colors?.customHex !== undefined &&
                theme?.colors?.customHex !== "#CC34BD"

              // If we're in presets tab and have a temporary selection, treat as not custom
              // This allows selecting presets even when customHex is stored
              const isCustom = hasStoredCustomColors && !selectedPreset

              const isSelected =
                !isCustom &&
                // If we have a temporary selection, only show that one as selected
                ((selectedPreset && selectedPreset === preset.id) ||
                  // If no temporary selection, show the stored preset as selected
                  (!selectedPreset && theme?.colors?.selectedPreset === preset.id))

              return (
                <div
                  key={preset.id}
                  className={`rounded-xl border-2 p-4 transition-all ${
                    isSelected ? "border-teal bg-teal/5" : "border-gray-light bg-white shadow-sm hover:shadow-md"
                  }`}>
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="mb-2">
                        <Typography variant="body" className="font-bold text-gray-dark">
                          {preset.name}
                        </Typography>
                        <Typography variant="small" className="mt-0.5 text-gray">
                          {preset.description}
                        </Typography>
                      </div>
                      <div className="flex items-center space-x-[-8px]">
                        {(() => {
                          const palette = generateColorPalette(preset.primaryColor)
                          const colors = [
                            palette.primary,
                            palette.bgDark,
                            palette.bgMedium,
                            palette.bgLight,
                            palette.bgExtraLight
                          ]
                          return colors.map((color, index) => (
                            <div
                              key={index}
                              className="h-8 w-8 rounded-full border-2 border-white shadow-sm"
                              style={{ backgroundColor: color }}
                              title={`${
                                index === 0
                                  ? "Primary"
                                  : index === 1
                                  ? "Bg Dark"
                                  : index === 2
                                  ? "Bg Medium"
                                  : index === 3
                                  ? "Bg Light"
                                  : "Bg Extra Light"
                              }: ${color}`}
                            />
                          ))
                        })()}
                      </div>
                    </div>

                    {isSelected ? (
                      <div className="flex items-center space-x-1.5">
                        <CheckIcon className="h-4 w-4 text-teal" />
                        <span className="text-base font-bold text-teal">Selected</span>
                      </div>
                    ) : (
                      <Button
                        type="primary"
                        size="small"
                        onClick={() => handlePresetSelect(preset.id)}
                        className="flex items-center justify-center px-4 py-2 text-center">
                        Select
                      </Button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {activeTab === "custom" && (
          <div className="space-y-6">
            {/* Custom Theme Description */}
            <div className="rounded-xl border border-gray-light bg-white p-4">
              <div className="mb-2">
                <Typography variant="body" className="font-bold text-gray-dark">
                  Custom Theme
                </Typography>
                <Typography variant="small" className="mt-0.5 text-gray">
                  Select a primary hex code and make it yours: vibrant, playful, and uniquely you!
                </Typography>
              </div>
            </div>

            {/* Color Picker and Input Section */}
            <div className="flex items-end space-x-4">
              {/* Color Picker */}
              <div className="flex-1">
                <Chrome
                  color={customHex}
                  onChange={(color) => {
                    setCustomHex(color.hex)
                  }}
                  style={{ width: "100%" }}
                />
              </div>

              {/* Hex Input Field */}
              <div className="w-60">
                <div className="mb-1">
                  <Typography variant="small" className="font-bold text-gray-dark">
                    Enter Primary Hex code
                  </Typography>
                </div>
                <input
                  type="text"
                  className="w-full rounded-lg border border-gray-light bg-white px-3 py-2 text-base font-normal text-gray-dark"
                  value={customHex.replace("#", "")}
                  onChange={(e) => {
                    const value = e.target.value
                    if (value.match(/^[0-9A-Fa-f]{0,6}$/)) {
                      setCustomHex(`#${value}`)
                    }
                  }}
                  maxLength={6}
                  placeholder="CC34BD"
                />
              </div>
            </div>

            {/* Theme Preview */}
            <div className="space-y-2">
              <Typography variant="small" className="font-bold text-gray-dark">
                Theme Preview
              </Typography>
              <div className="flex items-center space-x-[-8px]">
                {(() => {
                  const palette = generateColorPalette(customHex)
                  const colors = [
                    palette.primary,
                    palette.bgDark,
                    palette.bgMedium,
                    palette.bgLight,
                    palette.bgExtraLight
                  ]
                  return colors.map((color, index) => (
                    <div
                      key={index}
                      className="h-8 w-8 rounded-full border-2 border-white shadow-sm"
                      style={{ backgroundColor: color }}
                      title={`${
                        index === 0
                          ? "Primary"
                          : index === 1
                          ? "Bg Dark"
                          : index === 2
                          ? "Bg Medium"
                          : index === 3
                          ? "Bg Light"
                          : "Bg Extra Light"
                      }: ${color}`}
                    />
                  ))
                })()}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="sticky bottom-0 z-10 border-t border-gray-light bg-white p-8">
        <div className="flex items-center justify-end space-x-4">
          <Button type="secondary" onClick={handleCancelClick} className="px-4">
            Close
          </Button>
          <Button type="primary" onClick={handleSave} className="px-4">
            Save
          </Button>
        </div>
      </div>

      {/* Confirm Save Modal */}
      <ConfirmSaveModal
        visible={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmSave}
      />
    </div>
  )
}

export default ColorThemeForm
